import { useDeferredValue, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef, getCoreRowModel, getExpandedRowModel, useReactTable } from "@tanstack/react-table";
import { type ManagedTableState, type ManagedTableStateSetters } from "components/table/usePersistedTableState";
import { RT8ColumnSelector } from "components/core/blocks/MetricSelector";
import { SearchInput } from "components/fields/base/TextField";
import { RT8Pagination } from "components/table/Pagination";
import { Table } from "components/table/Table";
import { resetTable } from "components/table/utils";
import { RT8FilterWidget } from "views/merchandise/tables/FilterWidget";
import { Button } from "components/core/basic/Button";
import { MdRotateLeft } from "react-icons/md";
import ExpanderContainer from "components/core/basic/ExpanderContainer";
import { DevErrorDisplay } from "components/core/blocks/DevErrorDisplay";
import {
  type SearchQueryPerformanceV4Row,
  useGetV4SearchQueryPerformanceByQuery,
} from "./api/v4SearchQueryPerformanceReportByQuery";
import { Flex, HStack, VStack } from "../../../styled-system/jsx";
import { css } from "../../../styled-system/css";
import { type Moment } from "moment";
import { SearchQueryRowExpander } from "views/SearchQueryPerformance";
import useSearchTermReport from "hooks/useSearchTermReport";
import { useDates } from "dates/DatesContext";
import Loading from "components/core/blocks/Loading";

const Expander = ({
  row,
  start,
  end,
  preStart,
  preEnd,
}: {
  start: Moment;
  end: Moment;
  preStart: Moment;
  preEnd: Moment;
  row: {
    original: SearchQueryPerformanceV4Row;
  };
}) => {
  const { data: currentData, isLoading } = useSearchTermReport(
    row.original.search_query,
    start.format("YYYY-MM-DD"),
    end.format("YYYY-MM-DD"),
    1000,
    "query",
    {
      overrideFilterType: "iexact",
    },
  );

  const queryData = useMemo(() => {
    if (!currentData) {
      return [];
    }
    const matchingRow = currentData?.find(
      (strRow: { query: string; adv_targets: number[]; adv_keywords: number[] }) =>
        strRow.query === row.original.search_query,
    );

    return {
      ...row.original,
      adv_targets: matchingRow?.adv_targets.filter(Boolean) ?? [],
      adv_keywords: matchingRow?.adv_keywords.filter(Boolean) ?? [],
    };
  }, [currentData, row]);

  // How to join expander => it used to use the SQPR ID as a filter, then the STR to retrieve advertised keywords and targets
  return (
    <ExpanderContainer
      className={css({
        maxWidth: "calc(100vw - 140px)!important",
        padding: "medium",
        backgroundColor: "rgb(239, 239, 239)",
        overflow: "hidden",
      })}
    >
      <div className={css({ backgroundColor: "white" })}>
        {isLoading ?
          <Loading fullPage={false} height={"300px"} />
        : <SearchQueryRowExpander
            row={queryData}
            selectedReport={{
              start_date: start.format("YYYY-MM-DD"),
              end_date: end.format("YYYY-MM-DD"),
            }}
            selectedCompareReport={{
              start_date: preStart.format("YYYY-MM-DD"),
              end_date: preEnd.format("YYYY-MM-DD"),
            }}
          />
        }
      </div>
    </ExpanderContainer>
  );
};

const SearchQueryPerformanceDashboardTable = ({
  columns,
  state,
  initialState,
  setters,
  selectedAsins,
  setSelectedAsins,
  setShowAsinFilter,
  renderAsinsFilter = true,
  renderExpander = true,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<SearchQueryPerformanceV4Row, any>[];
  state: ManagedTableState;
  initialState: ManagedTableState;
  setters: ManagedTableStateSetters;
  selectedAsins: { product_id: string; asin: string }[];
  setSelectedAsins?: (ids: { product_id: string; asin: string; sku: string | null }[]) => void;
  setShowAsinFilter?: (show: boolean) => void;
  renderAsinsFilter?: boolean;
  renderExpander?: boolean;
}) => {
  const deferredSearch = useDeferredValue(state.globalFilter);
  const queryClient = useQueryClient();

  const bySearchQueryOptionsGetter = useGetV4SearchQueryPerformanceByQuery();

  const filters = useMemo(() => {
    const asinFilter =
      selectedAsins.length ?
        [
          {
            id: "asin",
            value: { op: "anyof", value: selectedAsins.map((asin) => asin.asin) },
          },
        ]
      : [];

    return [...state.columnFilters, ...asinFilter];
  }, [state.columnFilters, selectedAsins]);

  const searchQueryPerformanceByQueryReportQuery = useQuery(
    bySearchQueryOptionsGetter({
      pagination: state.pagination,
      sorting: state.sorting,
      filters,
      search: deferredSearch,
    }),
  );
  const { start, end, preStart, preEnd } = useDates();

  const formattedData = useMemo(() => {
    if (!searchQueryPerformanceByQueryReportQuery.data) {
      return [];
    }

    return searchQueryPerformanceByQueryReportQuery.data.data;
  }, [searchQueryPerformanceByQueryReportQuery.data]);

  void queryClient.prefetchQuery({
    ...bySearchQueryOptionsGetter({
      pagination: { pageIndex: state.pagination.pageIndex + 1, pageSize: state.pagination.pageSize },
      sorting: state.sorting,
      filters: state.columnFilters,
      search: deferredSearch,
    }),
    staleTime: 10000,
  });

  const table = useReactTable({
    data: formattedData,
    columns,
    defaultColumn: {
      filterFn: () => true,
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => renderExpander,
    getRowId: (row) => row.search_query,
    enableRowSelection: true,
    rowCount: searchQueryPerformanceByQueryReportQuery.data?.count || 0,
    initialState: initialState,
    state: state,
    ...setters,
    manualPagination: true,
    meta: {
      showSkeleton:
        searchQueryPerformanceByQueryReportQuery.isFetching &&
        (!searchQueryPerformanceByQueryReportQuery.data || searchQueryPerformanceByQueryReportQuery.isPlaceholderData),
      columnFilterSelectOptions: {},
    },
  });

  if (searchQueryPerformanceByQueryReportQuery.isError) {
    return (
      <DevErrorDisplay userMessage={"Error loading stats"} error={searchQueryPerformanceByQueryReportQuery.error} />
    );
  }

  return (
    <div>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={"Search"}
              isExpandable
              className={css({ flex: "0 1 350px" })}
              value={table.getState().globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        {renderAsinsFilter && setShowAsinFilter && (
          <VStack justifyContent={"center"}>
            <Button onPress={() => setShowAsinFilter(true)}>Filter by Product</Button>
            {selectedAsins.length ?
              <Flex gap={"medium"} alignItems={"center"}>
                <span className={css({ fontSize: "medium" })}>
                  {selectedAsins.length} ASIN{selectedAsins.length === 1 ? "" : "s"} Selected
                </span>
                <Button
                  variant={"link"}
                  className={css({ fontSize: "medium" })}
                  onPress={() => {
                    setSelectedAsins?.([]);
                  }}
                >
                  Clear
                </Button>
              </Flex>
            : null}
          </VStack>
        )}
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        className={css({ minWidth: "100%" })}
        renderExpander={
          renderExpander === false ? undefined : (
            (row) => {
              return <Expander row={row} preStart={preStart} preEnd={preEnd} end={end} start={start} />;
            }
          )
        }
      />
    </div>
  );
};

export default SearchQueryPerformanceDashboardTable;
